import { useState } from 'react'
import { toast } from 'sonner'
import { Button, Chip } from '@/components/ui'
import { parseDateOnly } from '@/lib/utils/date-only'
import {
  useConfirmSplit,
  useLogSplitEstimate,
  useSplitsForTracker,
  useUnsplit,
} from '../api/queries'
import type { ConfirmationSource, ShipConfirmationSplit } from '../api/types'
import { ConfirmSplitModal } from './ConfirmSplitModal'
import { LogSplitEstimateModal } from './LogSplitEstimateModal'
import { UnsplitConfirmModal } from './UnsplitConfirmModal'
import { STAGE_LABELS, STAGE_VARIANT } from '../utils/ship-confirmation-stage-meta'
import type { ShipConfirmationTracker } from '../api/types'

export interface SplitRowsPanelProps {
  tracker: ShipConfirmationTracker
}

function formatDate(iso: string | null): React.ReactNode {
  if (!iso) return <span className="text-tertiary">—</span>
  try {
    return parseDateOnly(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return iso
  }
}

/**
 * Expanded-row content for a split line (rendered via `DataTable`'s
 * `renderExpandedRow` — mirrors the Sales Inbox `IntakeLinesPreview`
 * shape, one level deeper: interactive, not read-only, since each split
 * has its own Confirm/Log estimate/Undo lifecycle). Fetches lazily —
 * only mounted while its parent row is expanded.
 */
export function SplitRowsPanel({ tracker }: SplitRowsPanelProps) {
  const { data: splits, isLoading } = useSplitsForTracker(tracker.id)
  const confirmSplit = useConfirmSplit()
  const logEstimate = useLogSplitEstimate()
  const unsplit = useUnsplit()

  const [confirmTarget, setConfirmTarget] = useState<ShipConfirmationSplit | null>(null)
  const [estimateTarget, setEstimateTarget] = useState<ShipConfirmationSplit | null>(null)
  const [unsplitOpen, setUnsplitOpen] = useState(false)

  if (isLoading) {
    return <div className="px-[var(--cell-padding-x)] py-3 text-sm text-tertiary">Loading splits…</div>
  }
  if (!splits || splits.length === 0) {
    return (
      <div className="px-[var(--cell-padding-x)] py-3 text-sm text-tertiary">
        No splits found for this line.
      </div>
    )
  }

  function submitConfirm(body: { confirmed_date: string; source: ConfirmationSource; detail?: string }) {
    if (!confirmTarget) return
    confirmSplit.mutate(
      { splitId: confirmTarget.id, body },
      {
        onSuccess: (result) => {
          toast.success(
            result.tracker.stage === 'confirmed'
              ? `Split ${confirmTarget.split_index} confirmed — all splits confirmed, mirrored to Odoo`
              : `Split ${confirmTarget.split_index} confirmed`,
          )
          setConfirmTarget(null)
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'confirmation failed', { duration: 6000 })
        },
      },
    )
  }

  function submitLogEstimate(body: { estimated_date?: string; note: string }) {
    if (!estimateTarget) return
    logEstimate.mutate(
      { splitId: estimateTarget.id, body },
      {
        onSuccess: () => {
          toast.success('Estimate update logged')
          setEstimateTarget(null)
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'failed to log update', { duration: 5000 })
        },
      },
    )
  }

  function submitUnsplit() {
    const force = splits!.some((s) => s.stage !== 'awaiting_confirmation')
    unsplit.mutate(
      { trackerId: tracker.id, force },
      {
        onSuccess: () => {
          toast.success(`${tracker.odoo_po_name} unsplit`)
          setUnsplitOpen(false)
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'unsplit failed', { duration: 5000 })
        },
      },
    )
  }

  return (
    <div className="overflow-x-auto px-[var(--cell-padding-x)] py-2">
      <table className="min-w-full text-sm">
        <thead className="text-role-table-header">
          <tr>
            <th className="px-2 py-1.5 text-left">Split</th>
            <th className="px-2 py-1.5 text-left">Qty</th>
            <th className="px-2 py-1.5 text-left">Stage</th>
            <th className="px-2 py-1.5 text-left">Ship Date</th>
            <th className="px-2 py-1.5 text-left">Source</th>
            <th className="px-2 py-1.5 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {splits.map((split) => (
            <tr key={split.id} className="border-t border-border-subtle">
              <td className="px-2 py-1.5">{split.split_index}</td>
              <td className="px-2 py-1.5">{split.quantity}</td>
              <td className="px-2 py-1.5">
                <Chip size="xs" variant={STAGE_VARIANT[split.stage]}>
                  {STAGE_LABELS[split.stage]}
                </Chip>
              </td>
              <td className="px-2 py-1.5">
                {split.stage === 'confirmed' || split.stage === 'received'
                  ? formatDate(split.confirmed_ship_date)
                  : split.latest_estimated_date
                    ? <span className="italic text-tertiary">Est. {formatDate(split.latest_estimated_date)}</span>
                    : <span className="text-tertiary">No date on file</span>}
              </td>
              <td className="px-2 py-1.5">{split.confirmation_source ?? <span className="text-tertiary">—</span>}</td>
              <td className="px-2 py-1.5">
                {split.stage === 'awaiting_confirmation' ? (
                  <div className="flex items-center gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setConfirmTarget(split)}>
                      Confirm
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEstimateTarget(split)}>
                      Log estimate
                    </Button>
                  </div>
                ) : split.stage === 'confirmed' ? (
                  <Button variant="secondary" size="sm" onClick={() => setConfirmTarget(split)}>
                    Revise
                  </Button>
                ) : (
                  <span className="text-tertiary">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-end pt-2">
        <Button variant="ghost" size="sm" onClick={() => setUnsplitOpen(true)}>
          Unsplit this line
        </Button>
      </div>

      <ConfirmSplitModal
        split={confirmTarget}
        isPending={confirmSplit.isPending}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={submitConfirm}
      />
      <LogSplitEstimateModal
        split={estimateTarget}
        isPending={logEstimate.isPending}
        onCancel={() => setEstimateTarget(null)}
        onSubmit={submitLogEstimate}
      />
      <UnsplitConfirmModal
        tracker={unsplitOpen ? tracker : null}
        splits={splits}
        isPending={unsplit.isPending}
        onCancel={() => setUnsplitOpen(false)}
        onConfirm={submitUnsplit}
      />
    </div>
  )
}
